import { useEffect, useState } from "react";
import { Tabs } from "antd";
import { useHistory, useLocation } from "react-router-dom";
import moment, { Moment } from "moment";
import Summary from "./Summary";
import Details from "./Detailed";
import ReportsFilters from "./Filters";
import ReportRangePicker, {
  DATE_FORMAT,
  findPresetLabel,
} from "./Filters/ReportRangePicker";

const TimeTrackingReports = () => {
  const history = useHistory();
  const location = useLocation();
  const activeTab =
    new URLSearchParams(location.search).get("tab") ?? "summary";

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    let dirty = false;
    if (!params.has("tab")) {
      params.set("tab", "summary");
      dirty = true;
    }
    if (!params.has("startDate")) {
      params.set("startDate", moment().startOf("isoWeek").format(DATE_FORMAT));
      dirty = true;
    }
    if (!params.has("endDate")) {
      params.set("endDate", moment().endOf("isoWeek").format(DATE_FORMAT));
      dirty = true;
    }
    if (dirty) {
      history.replace({ search: `?${params.toString()}` });
    }
  }, []);

  const handleTabChange = (key: string) => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("tab", key);
    history.replace({ search: `?${nextParams.toString()}` });
  };

  const handleApplyFilters = (filters: {
    projectIds: string[];
    employeeIds: string[];
  }) => {
    const nextParams = new URLSearchParams(history.location.search);
    if (filters.projectIds.length > 0) {
      nextParams.set("projectIds", filters.projectIds.join(","));
    } else {
      nextParams.delete("projectIds");
    }
    if (filters.employeeIds.length > 0) {
      nextParams.set("employeeIds", filters.employeeIds.join(","));
    } else {
      nextParams.delete("employeeIds");
    }
    history.replace({ search: `?${nextParams.toString()}` });
  };

  const initialParams = new URLSearchParams(location.search);
  const initialStart = initialParams.get("startDate");
  const initialEnd = initialParams.get("endDate");
  const [dateRange, setDateRange] = useState<[Moment, Moment]>(
    initialStart && initialEnd
      ? [moment(initialStart, DATE_FORMAT), moment(initialEnd, DATE_FORMAT)]
      : [moment().startOf("isoWeek"), moment().endOf("isoWeek")],
  );

  const handleDateChange = (range: [Moment, Moment]) => {
    setDateRange(range);
    const nextParams = new URLSearchParams(history.location.search);
    nextParams.set("startDate", range[0].format(DATE_FORMAT));
    nextParams.set("endDate", range[1].format(DATE_FORMAT));
    history.replace({ search: `?${nextParams.toString()}` });
  };

  return (
    <div>
      <ReportsFilters onApply={handleApplyFilters} />
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        destroyInactiveTabPane
        tabBarExtraContent={{
          right: (
            <ReportRangePicker
              value={dateRange}
              onChange={handleDateChange}
              initialActivePreset={findPresetLabel(dateRange[0], dateRange[1])}
            />
          ),
        }}
      >
        <Tabs.TabPane key="summary" tab="Summary">
          <Summary />
        </Tabs.TabPane>
        <Tabs.TabPane key="detailed" tab="Detailed">
          <Details />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default TimeTrackingReports;
